/**
 * Comprehensive Error Boundary System
 * Provides type-safe error handling and recovery mechanisms
 */

'use client';

import React, { Component, type ErrorInfo, type ReactNode } from 'react';

import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { navigateWithLocale } from '@/lib/i18n/locale-aware-path';

// ============================================================================
// ERROR BOUNDARY TYPES
// ============================================================================

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  retryCount: number;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
  level?: 'page' | 'component' | 'critical';
}

// ============================================================================
// MAIN ERROR BOUNDARY COMPONENT
// ============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Log in dev: use console.error only for the real Error (Turbopack attaches overlays to console.error lines).
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.group('🚨 Error Boundary Caught Error');
      console.error(error);
      if (error?.stack) {
        console.error(error.stack);
      }
      // Metadata: log (not error) so the IDE overlay points at the thrown error, not the ID line.
      // eslint-disable-next-line no-console
      console.log('Component stack:', errorInfo?.componentStack ?? '(none)');
      // eslint-disable-next-line no-console
      console.log('Error ID:', this.state.errorId);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange, resetKeys } = this.props;

    // Reset error boundary if props changed and resetOnPropsChange is true
    if (resetOnPropsChange && this.state.hasError) {
      const hasResetKeyChanged = resetKeys?.some(
        (key, index) => key !== prevProps.resetKeys?.[index],
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  override componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    const { onRetry } = this.props;

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    });

    if (onRetry) {
      onRetry();
    }
  };

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;

    if (this.state.retryCount < maxRetries) {
      this.setState(prevState => ({
        retryCount: prevState.retryCount + 1,
      }));

      // Reset after a short delay to allow state to update
      this.resetTimeoutId = window.setTimeout(() => {
        this.resetErrorBoundary();
      }, 100);
    }
  };

  override render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { children, fallback, maxRetries = 3, level = 'component' } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Render appropriate error UI based on level
      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          retryCount={retryCount}
          maxRetries={maxRetries}
          level={level}
          onRetry={this.handleRetry}
          onReset={this.resetErrorBoundary}
        />
      );
    }

    return children;
  }
}

// ============================================================================
// ERROR FALLBACK COMPONENT
// ============================================================================

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  maxRetries: number;
  level: 'page' | 'component' | 'critical';
  onRetry: () => void;
  onReset: () => void;
}

function ErrorFallback({
  error,
  errorInfo,
  retryCount,
  maxRetries,
  level,
  onRetry,
  onReset,
}: ErrorFallbackProps) {
  const isRetryable = retryCount < maxRetries;
  const isDevelopment = process.env.NODE_ENV === 'development';

  const getErrorTitle = () => {
    switch (level) {
      case 'page':
        return 'Page Error';
      case 'component':
        return 'Component Error';
      case 'critical':
        return 'Critical Error';
      default:
        return 'Application Error';
    }
  };

  const getErrorDescription = () => {
    switch (level) {
      case 'page':
        return 'Something went wrong while loading this page. Please try refreshing or contact support if the problem persists.';
      case 'component':
        return 'A component encountered an error. This might be a temporary issue.';
      case 'critical':
        return 'A critical error occurred. Please refresh the page or contact support immediately.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  return (
    <div
      className={`flex items-center justify-center ${
        level === 'page' ? 'min-h-screen' : 'min-h-[200px]'
      }`}
    >
      <div className='flex flex-col items-center space-y-6 text-center max-w-md mx-auto p-6'>
        {/* Error Icon */}
        <div className='flex flex-col items-center space-y-4'>
          <div
            className={`p-4 rounded-full ${
              level === 'critical'
                ? 'bg-destructive/10 text-destructive'
                : 'bg-orange-peel-100 text-orange-peel-600'
            }`}
          >
            <AlertTriangle className='h-8 w-8' />
          </div>

          <div>
            <h1 className='text-xl font-semibold text-foreground'>{getErrorTitle()}</h1>
            <p className='text-sm text-muted-foreground mt-2'>{getErrorDescription()}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col space-y-3 w-full'>
          {isRetryable && (
            <Button onClick={onRetry} className='w-full'>
              <RefreshCw className='h-4 w-4 mr-2' />
              Try Again ({retryCount + 1}/{maxRetries})
            </Button>
          )}

          <Button variant='outline' onClick={onReset} className='w-full'>
            <RefreshCw className='h-4 w-4 mr-2' />
            Reset Component
          </Button>

          {level === 'page' && (
            <Button variant='outline' onClick={() => navigateWithLocale('/')} className='w-full'>
              <Home className='h-4 w-4 mr-2' />
              Go to Homepage
            </Button>
          )}
        </div>

        {/* Development Error Details */}
        {isDevelopment && error && (
          <details className='w-full text-left'>
            <summary className='cursor-pointer text-sm text-muted-foreground mb-2'>
              <Bug className='h-4 w-4 inline mr-1' />
              Error Details (Development)
            </summary>
            <div className='bg-muted p-4 rounded-md text-xs font-mono overflow-auto max-h-64'>
              <div className='mb-2'>
                <strong>Error:</strong> {error.message}
              </div>
              {error.stack && (
                <div className='mb-2'>
                  <strong>Stack:</strong>
                  <pre className='whitespace-pre-wrap mt-1'>{error.stack}</pre>
                </div>
              )}
              {errorInfo?.componentStack && (
                <div>
                  <strong>Component Stack:</strong>
                  <pre className='whitespace-pre-wrap mt-1'>{errorInfo.componentStack}</pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SPECIALIZED ERROR BOUNDARIES
// ============================================================================

/**
 * Page-level error boundary for entire page components
 */
export function PageErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'level'>) {
  return (
    <ErrorBoundary level='page' {...props}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Component-level error boundary for individual components
 */
export function ComponentErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'level'>) {
  return (
    <ErrorBoundary level='component' {...props}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Critical error boundary for essential components
 */
export function CriticalErrorBoundary({ children, ...props }: Omit<ErrorBoundaryProps, 'level'>) {
  return (
    <ErrorBoundary level='critical' {...props}>
      {children}
    </ErrorBoundary>
  );
}

// ============================================================================
// ERROR BOUNDARY HOOKS
// ============================================================================

/**
 * Hook for error boundary state management
 */
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return {
    captureError,
    resetError,
  };
}

// ============================================================================
// ERROR REPORTING UTILITIES
// ============================================================================

/**
 * Report error with additional context
 * Note: Error reporting service can be integrated here if needed
 */
export function reportError(error: Error, context?: Record<string, unknown>) {
  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error reported:', error, context);
  }
  // Error reporting service integration can be added here
}

/**
 * Report error with user context
 * Note: Error reporting service can be integrated here if needed
 */
export function reportErrorWithUser(error: Error, userId?: string, userEmail?: string) {
  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error reported with user:', error, { userId, userEmail });
  }
  // Error reporting service integration can be added here
}
